// Inteligencia operativa de Sina: qué necesita atención, qué cambió desde la última
// revisión y por qué una foja no avanza. Todo puro, sin red, para poder testearlo.

const plural = (count, singular, pluralForm) => `${count} ${count === 1 ? singular : pluralForm}`;

const joinWithY = (items) => (items.length > 1 ? `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}` : items[0] || '');

const normalize = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .trim();

const URGENT_TONES = new Set(['critical', 'warning']);

const ALERT_DEFINITIONS = [
  { id: 'fojasAtrasadas', tone: 'critical', to: '/cargas/fojas-de-ruta', words: ['foja atrasada', 'fojas atrasadas'], count: (data) => (data.fojasSinSalida?.items || []).filter((foja) => foja.severidad && foja.severidad !== 'normal').length },
  { id: 'retirosVencidos', tone: 'critical', to: '/retiros', words: ['retiro vencido', 'retiros vencidos'], count: (data) => data.retiros?.vencidos },
  { id: 'repartosAnteriores', tone: 'warning', to: '/cargas/repartos', words: ['reparto de días anteriores sin cerrar', 'repartos de días anteriores sin cerrar'], count: (data) => data.repartos?.abiertosAnteriores },
  { id: 'porRecibir', tone: 'info', to: '/cargas/recepcion', words: ['guía por recibir', 'guías por recibir'], count: (data) => data.kpis?.porRecibir?.guias },
  { id: 'colaReparto', tone: 'info', to: '/cargas/repartos/nuevo', words: ['guía lista para repartir', 'guías listas para repartir'], count: (data) => data.entregas?.colaReparto?.guias },
  { id: 'lineasListas', tone: 'info', to: '/cargas/fojas-de-ruta', words: ['línea lista para despachar', 'líneas listas para despachar'], count: (data) => (data.lineas || []).filter((linea) => linea.estado === 'lista').length },
];

// Los pendientes no dependen de la pantalla del tablero: alcanza con poder abrir
// alguna de las pantallas a las que llevan las alertas.
export function canSeePendingWork(canOpen) {
  return ALERT_DEFINITIONS.some((definition) => canOpen(definition.to));
}

export function buildOperationalAlerts(dashboard) {
  if (!dashboard) return [];
  return ALERT_DEFINITIONS
    .map(({ id, tone, to, words, count }) => ({ id, tone, to, words, count: Number(count(dashboard)) || 0 }))
    .filter((alert) => alert.count > 0)
    .map(({ id, tone, to, words, count }) => ({ id, tone, count, label: plural(count, ...words), to }));
}

const SUMMARY_PATTERN = /(pendiente|que tengo|que hay|resumen|como venimos|como estamos|como vamos|alerta|atrasad|vencid|sin salida|por recibir|para repartir|para despachar|urgente)/;

// Las preguntas con número (guía, foja, reparto) siguen yendo a la búsqueda de registros.
export function isSummaryQuery(value) {
  const text = normalize(value);
  if (!text || /\d/.test(text) || /^(cliente|client)\b/.test(text)) return false;
  return SUMMARY_PATTERN.test(text);
}

export function buildPendingSummary(alerts = []) {
  if (!alerts.length) {
    return {
      title: 'Todo al día',
      message: 'No veo fojas atrasadas, retiros vencidos ni repartos sin cerrar. ¡Buen trabajo!',
      sinaState: 'success',
      alerts: [],
    };
  }
  const urgent = alerts.filter((alert) => alert.tone === 'critical').map((alert) => alert.label);
  const rest = alerts.filter((alert) => alert.tone !== 'critical').map((alert) => alert.label);
  const parts = [];
  if (urgent.length) parts.push(`Lo más urgente: ${joinWithY(urgent)}.`);
  if (rest.length) parts.push(`${urgent.length ? 'Además' : 'Nada urgente. Tenés'}: ${joinWithY(rest)}.`);
  return {
    title: 'Tus pendientes',
    message: parts.join(' '),
    sinaState: urgent.length ? 'warning' : 'help',
    alerts,
  };
}

// Lo urgente que el usuario puede ir a resolver: alimenta el globito del avatar.
export function selectUrgentAlerts(alerts = [], canOpen = () => true) {
  return alerts.filter((alert) => URGENT_TONES.has(alert.tone) && canOpen(alert.to));
}

export function alertCounts(alerts = []) {
  return Object.fromEntries(alerts.map((alert) => [alert.id, alert.count]));
}

// Sin revisión previa no hay con qué comparar: la primera vuelta solo fija la base.
export function findNewAlerts(previousCounts, alerts = []) {
  if (!previousCounts) return [];
  return alerts.filter((alert) => URGENT_TONES.has(alert.tone) && alert.count > (Number(previousCounts[alert.id]) || 0));
}

export function buildProactiveNotice(newAlerts = []) {
  if (!newAlerts.length) return null;
  return {
    title: 'Ojo, apareció algo nuevo',
    message: `Ahora hay ${joinWithY(newAlerts.map((alert) => alert.label))}.`,
    to: newAlerts[0].to,
  };
}

const FOJA_STAGES = [
  ['armado', 'Armado'],
  ['emision', 'Emisión'],
  ['despacho', 'Despacho confirmado'],
  ['recepcion', 'Recepción en destino'],
];

const FOJA_STAGE_STATUS = {
  borrador: ['current', 'pending', 'pending', 'pending'],
  cerrada: ['completed', 'completed', 'current', 'pending'],
  despachada_confirmada: ['completed', 'completed', 'completed', 'current'],
  recibida_parcial: ['completed', 'completed', 'completed', 'blocked'],
  recibida: ['completed', 'completed', 'completed', 'completed'],
  anulada: ['not_applicable', 'not_applicable', 'not_applicable', 'not_applicable'],
};

const NOT_DEPARTED = new Set(['borrador', 'cerrada']);
const STALE_HOURS = 24;

const FOJA_ACTIONS = {
  OPEN_FOJA: { label: 'Abrir foja', route: (id) => `/cargas/fojas-de-ruta?id=${id}` },
  CONFIRM_DISPATCH: { label: 'Confirmar despacho', route: () => '/cargas/confirmacion-de-despachos' },
  OPEN_RECEPTION: { label: 'Ver recepción', route: (id) => `/fojas/recepcion/${id}` },
};

const FOJA_STATE_ACTIONS = {
  borrador: ['OPEN_FOJA'],
  cerrada: ['CONFIRM_DISPATCH', 'OPEN_FOJA'],
  despachada_confirmada: ['OPEN_RECEPTION', 'OPEN_FOJA'],
  recibida_parcial: ['OPEN_RECEPTION', 'OPEN_FOJA'],
  recibida: ['OPEN_FOJA'],
  anulada: ['OPEN_FOJA'],
};

const hoursSince = (value, now) => {
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? Math.floor((now.getTime() - time) / 3_600_000) : null;
};

export function buildFojaDiagnosis(detail, { now = new Date(), canOpen = () => false } = {}) {
  const foja = detail?.foja || detail || {};
  const estado = normalize(foja.estado);
  const numero = foja.numero || foja.id;
  const totalGuias = Number(detail?.stats?.total_guias ?? foja.total_guias ?? detail?.guias?.length) || 0;
  const recibidas = Number(detail?.stats?.total_guias_recibidas ?? foja.total_guias_recibidas) || 0;
  const hasChofer = Boolean(foja.chofer_id || foja.chofer_nombre);
  const hasUnidad = Boolean(foja.vehiculo_id || foja.unidad_id || foja.unidad_dominio);
  const destino = foja.sucursal_destino_nombre ? ` (${foja.sucursal_destino_nombre})` : '';
  const guiasLabel = plural(totalGuias, 'guía', 'guías');

  const missingSteps = [];
  const warnings = [];
  let title;
  let summary;

  if (estado === 'borrador') {
    title = 'En armado';
    summary = `La foja #${numero} está en armado con ${guiasLabel}. Todavía no se emitió.`;
    if (!totalGuias) missingSteps.push('Cargar al menos una guía.');
    if (!hasChofer) missingSteps.push('Asignar un chofer.');
    if (!hasUnidad) missingSteps.push('Asignar una unidad.');
  } else if (estado === 'cerrada') {
    title = 'Emitida';
    summary = `La foja #${numero} ya se emitió con ${guiasLabel}. Falta confirmar el despacho para que salga.`;
    if (!hasChofer) warnings.push('No tiene chofer asignado.');
    if (!hasUnidad) warnings.push('No tiene unidad asignada.');
  } else if (estado === 'despachada_confirmada') {
    title = 'En viaje';
    summary = `La foja #${numero} está en viaje. Falta que la reciban en destino${destino}.`;
  } else if (estado === 'recibida_parcial') {
    title = 'Recepción parcial';
    summary = `Recibieron ${recibidas} de ${totalGuias} guías. Faltan ${Math.max(totalGuias - recibidas, 0)}: revisá la recepción.`;
  } else if (estado === 'recibida') {
    title = 'Recibida';
    summary = `La foja #${numero} llegó completa: ${guiasLabel} recibidas.`;
  } else if (estado === 'anulada') {
    title = 'Anulada';
    summary = `La foja #${numero} fue anulada; queda solo como registro.`;
  } else {
    title = 'Sin estado claro';
    summary = `No reconozco el estado "${foja.estado || 'vacío'}" de la foja #${numero}. Abrila para revisarla.`;
  }

  const age = NOT_DEPARTED.has(estado) ? hoursSince(foja.created_at, now) : null;
  if (age !== null && age >= STALE_HOURS) warnings.push(`Lleva ${age} h sin salir.`);

  const statuses = FOJA_STAGE_STATUS[estado] || FOJA_STAGE_STATUS.anulada;
  const stages = FOJA_STAGES.map(([key, label], index) => ({ key, label, status: statuses[index] }));
  const availableActions = (FOJA_STATE_ACTIONS[estado] || ['OPEN_FOJA'])
    .map((key) => ({ key, label: FOJA_ACTIONS[key].label, route: FOJA_ACTIONS[key].route(foja.id) }))
    .filter((item) => canOpen(item.route));

  const needsAttention = missingSteps.length || warnings.length || estado === 'recibida_parcial';
  return {
    title,
    conversational: summary,
    stages,
    missingSteps,
    warnings,
    availableActions,
    sinaState: needsAttention ? 'warning' : estado === 'recibida' ? 'success' : 'help',
  };
}
